/**
 * Pure computation functions for grocery list rendering.
 * Shared between WeekGroceryDrawer and SessionsPage grocery drawer.
 */

import type { GroceryItem, GroceryRepresentation } from "@/client/api";
import { formatRange } from "@/client/utils/formatting";

/** Format a single grocery representation (quantity + optional). */
export function fmtRep(rep: GroceryRepresentation): string {
  let main = formatRange(rep.quantity, rep.quantity_max, rep.unit);
  if (rep.optional_quantity != null) {
    main += ` + ${formatRange(rep.optional_quantity, rep.optional_quantity_max, rep.unit)} opc.`;
  }
  return main;
}

/** Get the display quantity for an item given the current cycling index. */
export function getDisplayQty(
  item: GroceryItem,
  repIndex: Record<string, number>,
): { qty: string; canCycle: boolean } {
  if (item.representations.length === 0) return { qty: "", canCycle: false };
  const idx = repIndex[item.ingredient_id] ?? 0;
  const rep = item.representations[idx % item.representations.length]!;
  return { qty: fmtRep(rep), canCycle: item.representations.length > 1 };
}

/** Build markdown clipboard text for a flat grocery list (session-style). */
export function buildClipboardText(
  items: Record<string, GroceryItem[]>,
  repIndex: Record<string, number>,
  typeOrder: string[],
  typeEmojis: Record<string, string>,
  typeLabels: Record<string, string>,
): string {
  const lines: string[] = [];
  for (const type of typeOrder) {
    const typeItems = items[type];
    if (!typeItems?.length) continue;
    lines.push(`${typeEmojis[type] ?? "📦"} **${typeLabels[type] ?? type}**`);
    for (const item of typeItems) {
      const { qty } = getDisplayQty(item, repIndex);
      const descStr = item.descriptive.length > 0 ? item.descriptive.join(" + ") : "";
      const fallback = qty ? (descStr ? `${qty} + ${descStr}` : qty) : descStr;
      const recipeHint = item.recipes.length > 1 ? ` _(${item.recipes.join(", ")})_` : "";
      lines.push(`- [ ] ${item.name}${fallback ? ` — ${fallback}` : ""}${recipeHint}`);
    }
    lines.push("");
  }
  return lines.join("\n").trim();
}

/** Build markdown clipboard text for a day-grouped grocery list (week-style). */
export function buildDayClipboard(
  day: string,
  dayLabel: string,
  items: Record<string, GroceryItem[]>,
  repIndex: Record<string, number>,
  typeEmojis: Record<string, string>,
  typeLabels: Record<string, string>,
): string {
  const lines: string[] = [`## ${dayLabel}`];
  for (const [type, typeItems] of Object.entries(items)) {
    lines.push(`${typeEmojis[type] ?? "📦"} **${typeLabels[type] ?? type}**`);
    for (const item of typeItems) {
      const rep =
        item.representations[
          (repIndex[item.ingredient_id] ?? 0) % (item.representations.length || 1)
        ];
      const repStr = rep ? fmtRep(rep) : "";
      const descStr = item.descriptive.length > 0 ? item.descriptive.join(" + ") : "";
      const qty = repStr ? (descStr ? `${repStr} + ${descStr}` : repStr) : descStr;
      lines.push(`- [ ] ${item.name}${qty ? ` — ${qty}` : ""}`);
    }
  }
  return lines.join("\n");
}
